import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { AxisLeft, AxisBottom, AxisRight } from '@visx/axis';
import { Group } from '@visx/group';
import { scaleLinear, scaleUtc, scaleBand } from '@visx/scale';
import * as React from 'react';
import React__default, { useMemo, useRef, useState, useEffect, useCallback, memo, useReducer } from 'react';
import { DesirabilityFunctionType, DTOperation, ConditionType, SCORING_FUNCTIONS_RULES } from '@discngine/moosa-models';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Bar, LinePath } from '@visx/shape';
import { getTextWidth, calculateHistogramTicks, rgbToString } from '@discngine/moosa-common';
import { localPoint } from '@visx/event';
import truncate from 'lodash/truncate';
import { Drag } from '@visx/drag';
import { BoxPlot } from '@visx/stats';

const HISTOGRAM_BAR = '#004D67';
const HISTOGRAM_SELECTED_AREA = '#429198';
const EMPTY_DATA_TEXT = '#A6A6A6';

var _ellipse, _path, _path2, _path3;
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
var SvgEmpty = function SvgEmpty(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    xmlns: "http://www.w3.org/2000/svg",
    width: 56,
    height: 36,
    fill: "none"
  }, props), _ellipse || (_ellipse = /*#__PURE__*/React.createElement("ellipse", {
    cx: 28,
    cy: 28.708,
    rx: 27.5,
    ry: 6.458,
    fill: "#E8E8E8"
  })), _path || (_path = /*#__PURE__*/React.createElement("path", {
    d: "M8.5 12.111 17.529 1.49c.263-.31.65-.489 1.058-.489h18.857c.406 0 .791.177 1.055.486l9.098 10.625v16.25A2.639 2.639 0 0 1 44.958 31h-33.82A2.639 2.639 0 0 1 8.5 28.361v-16.25Z",
    fill: "#fff"
  })), _path2 || (_path2 = /*#__PURE__*/React.createElement("path", {
    d: "M8.5 12.111 17.529 1.49c.263-.31.65-.489 1.058-.489h18.857c.406 0 .791.177 1.055.486l9.098 10.625m-39.097 0h8.951c.756 0 1.431.47 1.694 1.18l.902 2.438a2.222 2.222 0 0 0 2.084 1.452h11.635c.96 0 1.812-.617 2.111-1.529l.756-2.3a1.805 1.805 0 0 1 1.716-1.24h9.248m-39.097 0v16.25A2.639 2.639 0 0 0 11.139 31h33.82a2.639 2.639 0 0 0 2.638-2.639v-16.25",
    stroke: "#E1E1E1",
    strokeWidth: 1.111
  })), _path3 || (_path3 = /*#__PURE__*/React.createElement("path", {
    d: "M38.349 12.111h8.693c.306 0 .555.25.555.556V28.36a2.639 2.639 0 0 1-2.639 2.64h-33.82A2.639 2.639 0 0 1 8.5 28.36V12.667c0-.307.249-.556.556-.556h8.395c.756 0 1.431.47 1.694 1.18l.902 2.438a2.222 2.222 0 0 0 2.084 1.452h11.635c.96 0 1.812-.617 2.111-1.528l.756-2.3a1.805 1.805 0 0 1 1.716-1.242Z",
    fill: "#FBFBFB",
    stroke: "#E1E1E1",
    strokeWidth: 1.111
  })));
};

const EmptyHistogramNotification = (props) => {
    const { width, height } = props;
    return (jsxs(Group, { children: [jsx(Group, { transform: `translate(${(width - 55) / 2}, ${(height - 34) / 2 - 25})`, children: jsx(SvgEmpty, {}) }), jsx("text", { fill: EMPTY_DATA_TEXT, fontFamily: "Roboto, sans-serif", textAnchor: "middle", transform: "translate(0, 15)", x: "50%", y: "50%", children: "No data to display" })] }));
};

const re = /(?<sign>[+-]{1})?(?<intPart>\d+).(?<floatPart>\d+)e?(?<exp>[+\-\d]+)?/;
const createRegexReplacer = (precision) => {
    return (_match, sign, intPart, floatPart, exp) => {
        if (/[1-9]+/.test(floatPart) && !floatPart.startsWith('0'.repeat(precision))) {
            return `${sign || ''}${intPart}.${floatPart.slice(0, precision - 1)}e${exp}`;
        }
        return `${sign || ''}${intPart}e${exp}`;
    };
};
const isExponential = (num) => {
    return /[+-]?\d+[.]?[\d]*e[+-]?\d+/.test(num.toString());
};
const toPrecisionString = (num, precision) => {
    if (isNaN(num)) {
        return 'NaN';
    }
    const regExReplacer = createRegexReplacer(precision);
    if (Number.isInteger(num)) {
        const numberLength = num.toString().length;
        if (numberLength <= precision) {
            return num.toString();
        }
        return num.toExponential().replace(re, regExReplacer);
    }
    if (isExponential(num)) {
        return num.toString().replace(re, regExReplacer);
    }
    const [integerPart, floatPart] = num.toString().split('.');
    if (integerPart === '0') {
        if (floatPart.length < precision) {
            return parseFloat(num.toString()).toString();
        }
        return num.toExponential().replace(re, regExReplacer);
    }
    if (integerPart.length < precision) {
        const flPart = floatPart.slice(0, precision - integerPart.length);
        return `${integerPart}.${flPart}`;
    }
    if (integerPart.length === precision) {
        return integerPart.toString();
    }
    return Number(integerPart).toExponential().replace(re, regExReplacer);
};

const SlicedBar = ({ x, y, width, yScale, colors }) => {
    let maxHeight = yScale(0);
    let maxY = maxHeight;
    return (jsx(Group, { children: y.map((y, index) => {
            const barHeight = maxHeight - yScale(y);
            const barY = maxY - barHeight;
            maxY = barY;
            if (barHeight < 0)
                return null;
            return (jsx(Bar, { fill: colors[index]?.color || HISTOGRAM_BAR, height: barHeight, width: width, x: x, y: barY }, `bar-${x}-${index}`));
        }) }));
};

const DiscreteSlicedBar = ({ data, xScale, yScale, component, }) => {
    return (jsx(Fragment, { children: data?.bars.map((bar) => {
            const barWidth = xScale.bandwidth();
            const barX = xScale(bar.x);
            if (!barX)
                return null;
            return (jsxs(Group, { children: [jsx(SlicedBar, { colors: data.colors, width: barWidth, x: barX, y: bar.y, yScale: yScale }), component?.({ bar, barWidth, barX })] }, `bar-${barX}`));
        }) }));
};

const NUMERIC_BAR_GAP = 2;
const numericHistogramDataToBars = (data, xScale, numericBarGap) => {
    return data?.bars.map(({ x, y }) => {
        let barX0, barWidth;
        if (x[0] !== x[1]) {
            barX0 = xScale(x[0]) + numericBarGap / 2;
            const barX1 = xScale(x[1]) - numericBarGap / 2;
            barWidth = barX1 - barX0;
        }
        else {
            barWidth = numericBarGap * 10;
            barX0 = xScale(x[0]) - barWidth / 2;
        }
        return {
            x: barX0,
            y,
            width: barWidth,
        };
    });
};
const NumericSlicedBar = ({ data, xScale, yScale, numericBarGap = NUMERIC_BAR_GAP, }) => {
    const bars = useMemo(() => {
        return numericHistogramDataToBars(data, xScale, numericBarGap);
    }, [data, numericBarGap, xScale]);
    return (jsx(Fragment, { children: data &&
            bars?.map((bar, index) => {
                return (bar.width && (jsx(SlicedBar, { colors: data.colors, width: bar.width, x: bar.x, y: bar.y, yScale: yScale }, index)));
            }) }));
};

dayjs.extend(utc);
const NUM_TICKS_Y = 6;
const NUM_TICKS_X = 8;
const SMALL_NUM_TICKS_Y = 3;
const SMALL_NUM_TICKS_X = 4;
const MARGIN$1 = { left: 36, right: 10, top: 10, bottom: 56 };
const TOOLTIP = {
    width: 16,
    padding: 16,
};
const tickLabelProps = () => {
    return {
        dy: 8,
        angle: -45,
        fontSize: 9,
        textAnchor: 'middle',
    };
};
const tickFormatter = (value) => {
    return toPrecisionString(Number(value), 4);
};
/**
 * Format date to display on histogram.
 * All dates should be parsed as UTC dates without timezone to be compatible with backend representation
 * @param date Date instance or Unix timestamp number
 */
const formatDateForHistogram = (date) => {
    return dayjs.utc(date).format('YYYY-MM-DD');
};
const dateTickFormatter = (value) => {
    return formatDateForHistogram(value.valueOf());
};
const getBarsRerenderForDesirabilityHistogram = (desirabilityType, histogramData) => {
    if (desirabilityType === DesirabilityFunctionType.discrete) {
        return (xScale, yScale) => (jsx(DiscreteSlicedBar, { data: histogramData, xScale: xScale, yScale: yScale }));
    }
    else {
        return (xScale, yScale) => (jsx(NumericSlicedBar, { data: histogramData, xScale: xScale, yScale: yScale }));
    }
};

var e=[],t=[];function n(n,r){if(n&&"undefined"!=typeof document){var a,s=!0===r.prepend?"prepend":"append",d=!0===r.singleTag,i="string"==typeof r.container?document.querySelector(r.container):document.getElementsByTagName("head")[0];if(d){var u=e.indexOf(i);-1===u&&(u=e.push(i)-1,t[u]={}),a=t[u]&&t[u][s]?t[u][s]:t[u][s]=c();}else a=c();65279===n.charCodeAt(0)&&(n=n.substring(1)),a.styleSheet?a.styleSheet.cssText+=n:a.appendChild(document.createTextNode(n));}function c(){var e=document.createElement("style");if(e.setAttribute("type","text/css"),r.attributes)for(var t=Object.keys(r.attributes),n=0;n<t.length;n++)e.setAttribute(t[n],r.attributes[t[n]]);var a="prepend"===s?"afterbegin":"beforeend";return i.insertAdjacentElement(a,e),e}}

var css$2 = ".NumericHistogram_module_plot__6096d16d {\n  user-select: none;\n  overflow: visible;\n}\n.NumericHistogram_module_handler__6096d16d {\n  cursor: ew-resize;\n}\n.NumericHistogram_module_tooltip__6096d16d {\n  filter: drop-shadow(0px 6px 16px rgba(0, 0, 0, 0.5));\n  fill: #FFFFFF;\n  pointer-events: none;\n}\n.NumericHistogram_module_tooltipText__6096d16d {\n  font-size: 9px;\n  transform: rotate(-90deg);\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  width: 100%;\n  height: 100%;\n  pointer-events: none;\n  white-space: nowrap;\n}\n";
var modules_30e9243f = {"plot":"NumericHistogram_module_plot__6096d16d","handler":"NumericHistogram_module_handler__6096d16d","tooltip":"NumericHistogram_module_tooltip__6096d16d","tooltipText":"NumericHistogram_module_tooltipText__6096d16d"};
n(css$2,{});

const GREATER = [DTOperation.Greater, DTOperation.GreaterEqual];
const NumericHistogramInput = ({ width, height, xScale, condition, onChange, dataType, }) => {
    const ref = useRef(null);
    const [boundingRect, setBoundingRect] = useState(null);
    const [localCondition, setLocalCondition] = useState(condition);
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState(null);
    const yMax = height - MARGIN$1.bottom;
    const xMax = width - MARGIN$1.right;
    const maxHeight = Math.max(yMax - MARGIN$1.top, 0);
    useEffect(() => {
        setBoundingRect(ref.current?.getBoundingClientRect() || null);
    }, []);
    useEffect(() => {
        setLocalCondition(condition);
    }, [condition]);
    const areaPositions = useMemo(() => {
        switch (localCondition?.type) {
            case ConditionType.Simple: {
                if (GREATER.includes(localCondition.operation)) {
                    const min = Math.max(MARGIN$1.left, Math.min(xMax, xScale(localCondition.threshold)));
                    const max = xMax - min;
                    return [{ min, max, width: Math.max(0, max) }];
                }
                const min = MARGIN$1.left;
                const max = Math.max(MARGIN$1.left, Math.min(xMax, xScale(localCondition.threshold)));
                return [{ min, max, width: Math.max(0, max - min) }];
            }
            case ConditionType.Range: {
                const minThresholdX = Number.isFinite(localCondition.min.threshold)
                    ? xScale(localCondition.min.threshold)
                    : MARGIN$1.left;
                const maxThresholdX = Number.isFinite(localCondition.max.threshold)
                    ? xScale(localCondition.max.threshold)
                    : xMax;
                if (!localCondition.isInverted) {
                    const min = Math.max(MARGIN$1.left, Math.min(xMax, minThresholdX));
                    const max = Math.max(MARGIN$1.left, Math.min(xMax, maxThresholdX));
                    return [{ min, max, width: Math.max(0, max - min) }];
                }
                else {
                    const min = Math.max(MARGIN$1.left, minThresholdX);
                    const max = Math.max(MARGIN$1.left, maxThresholdX);
                    return [
                        { min: MARGIN$1.left, max: min, width: Math.max(0, min - MARGIN$1.left) },
                        { min: max, max: xMax, width: Math.max(0, xMax - max) },
                    ];
                }
            }
            default:
                return [{ min: 0, max: 0, width: 0 }];
        }
    }, [localCondition, xMax, xScale]);
    const handlersPosition = useMemo(() => {
        if (localCondition?.type === ConditionType.Simple) {
            const x = xScale(localCondition.threshold);
            return [Math.max(MARGIN$1.left, Math.min(xMax, x))];
        }
        if (localCondition?.type === ConditionType.Range) {
            const min = Number.isFinite(localCondition.min.threshold)
                ? xScale(localCondition.min.threshold)
                : MARGIN$1.left;
            const max = Number.isFinite(localCondition.max.threshold)
                ? xScale(localCondition.max.threshold)
                : xMax;
            return [
                Math.max(MARGIN$1.left, Math.min(xMax, min)),
                Math.max(MARGIN$1.left, Math.min(xMax, max)),
            ];
        }
        return [];
    }, [localCondition, xMax, xScale]);
    const onPointerDown = (newPosition) => {
        setPosition(newPosition);
        setIsDragging(true);
    };
    const onPointerUp = useCallback(() => {
        setIsDragging(false);
        setPosition(null);
        localCondition && onChange?.(localCondition);
    }, [localCondition, onChange]);
    const onPointerMove = useCallback((event) => {
        if (!isDragging || !ref.current) {
            return;
        }
        let { x } = localPoint(event) ?? { x: 0 };
        if (boundingRect) {
            const { x: refX, width: refW } = boundingRect;
            if (event.clientX < refX) {
                x = 0;
            }
            else if (event.clientX > refX + refW) {
                x = xMax;
            }
        }
        const threshold = xScale.invert(x).valueOf();
        setLocalCondition((condition) => {
            if (condition?.type === ConditionType.Simple) {
                return { ...condition, threshold };
            }
            if (condition?.type === ConditionType.Range && position) {
                if (position === 'max' && threshold < condition.min.threshold) {
                    return {
                        ...condition,
                        max: { ...condition.max, threshold: condition.min.threshold },
                        min: { ...condition.min, threshold },
                    };
                }
                if (position === 'min' && threshold > condition.max.threshold) {
                    return {
                        ...condition,
                        max: { ...condition.max, threshold },
                        min: { ...condition.min, threshold: condition.max.threshold },
                    };
                }
                return {
                    ...condition,
                    [position]: { ...condition[position], threshold },
                };
            }
        });
    }, [boundingRect, isDragging, position, xMax, xScale]);
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mouseup', onPointerUp);
            document.addEventListener('mousemove', onPointerMove);
        }
        return () => {
            document.removeEventListener('mouseup', onPointerUp);
            document.removeEventListener('mousemove', onPointerMove);
        };
    }, [isDragging, onPointerMove, onPointerUp]);
    return (jsxs("g", { ref: ref, children: [areaPositions.map(({ min, width }, index) => (jsx("rect", { fill: HISTOGRAM_SELECTED_AREA, height: maxHeight, opacity: 0.3, width: width, x: min, y: MARGIN$1.top }, index))), jsx("rect", { ...(isDragging && { className: modules_30e9243f.handler }), fill: "transparent", height: height, width: width, x: 0, y: 0 }), handlersPosition.map((position, idx) => {
                const label = dataType === 'numeric'
                    ? xScale.invert(position).toFixed(2)
                    : formatDateForHistogram(xScale.invert(position));
                const labelWidth = getTextWidth(label) + TOOLTIP.padding;
                return (jsxs("g", { children: [jsx("line", { className: modules_30e9243f.handler, stroke: HISTOGRAM_SELECTED_AREA, strokeWidth: 2, x1: position, x2: position, y1: MARGIN$1.top, y2: yMax }), jsx("line", { className: modules_30e9243f.handler, stroke: "transparent", strokeWidth: 6, x1: position, x2: position, y1: MARGIN$1.top, y2: yMax, onPointerDown: () => onPointerDown(idx === 0 ? 'min' : 'max') }), isDragging && (jsxs("g", { children: [jsx("rect", { className: modules_30e9243f.tooltip, height: labelWidth, rx: 4, width: TOOLTIP.width, x: position - TOOLTIP.width / 2, y: (yMax - MARGIN$1.top) / 2 }), jsx("foreignObject", { height: labelWidth, style: { pointerEvents: 'none' }, width: TOOLTIP.width, x: position - TOOLTIP.width / 2, y: (yMax - MARGIN$1.top) / 2, children: jsx("div", { className: modules_30e9243f.tooltipText, children: label }) })] }))] }, `${position}-${idx}`));
            })] }));
};

const DateHistogram = React__default.memo(({ data, height, width, condition, onChange, isSmall = false }) => {
    const MAX_TICKS = useMemo(() => (isSmall ? SMALL_NUM_TICKS_X : NUM_TICKS_X), [isSmall]);
    const yMax = height - MARGIN$1.bottom;
    const xMax = width - MARGIN$1.right;
    const yValueMax = useMemo(() => {
        return Math.max(...data.bars.map(({ y }) => y.reduce((sum, y) => sum + y, 0)));
    }, [data]);
    const xRange = useMemo(() => {
        const range = { min: Infinity, max: -Infinity, delta: 0, dataWidth: 0 };
        for (const { x } of data.bars) {
            if (x[0] < range.min) {
                range.min = x[0];
            }
            if (x[1] > range.max) {
                range.max = x[1];
            }
        }
        range.dataWidth = range.max - range.min;
        range.delta = range.dataWidth * 0.01 || range.max * 0.01;
        if (data.bars.length < 6) {
            range.delta = (range.dataWidth / data.bars.length) * ((6 - data.bars.length) / 2);
        }
        return range;
    }, [data]);
    const yScale = useMemo(() => {
        return scaleLinear({ range: [MARGIN$1.top, yMax], domain: [yValueMax, 0] });
    }, [yValueMax, yMax]);
    const xScale = useMemo(() => {
        return scaleUtc({
            range: [MARGIN$1.left, xMax],
            domain: [xRange.min - xRange.delta, xRange.max + xRange.delta],
        });
    }, [xMax, xRange]);
    const tickValues = useMemo(() => {
        if (data.bars.length > 0 && data.bars.length < MAX_TICKS) {
            const values = data.bars.map(({ x }) => x[0]);
            values.push(data.bars[data.bars.length - 1].x[1]);
            return values;
        }
    }, [MAX_TICKS, data.bars]);
    const numTicks = data.bars.length > MAX_TICKS ? MAX_TICKS : data.bars.length + 1;
    return (jsxs("svg", { className: modules_30e9243f.plot, height: height, width: width, xmlns: "http://www.w3.org/2000/svg", children: [yValueMax <= 0 && jsx(EmptyHistogramNotification, { height: height, width: width }), yValueMax > 0 && (jsxs(Group, { children: [jsx(Group, { children: jsx(NumericSlicedBar, { data: data, numericBarGap: isSmall ? 0 : undefined, xScale: xScale, yScale: yScale }) }), jsx(Group, { left: MARGIN$1.left, children: jsx(AxisLeft, { hideZero: true, numTicks: isSmall ? SMALL_NUM_TICKS_Y : NUM_TICKS_Y, scale: yScale }) }), jsx(Group, { top: yMax, children: jsx(AxisBottom, { numTicks: numTicks, scale: xScale, tickFormat: dateTickFormatter, tickLabelProps: tickLabelProps, tickValues: tickValues }) }), jsx(NumericHistogramInput, { condition: condition, dataType: 'date', height: height, width: width, xScale: xScale, onChange: onChange })] }))] }));
});

const NumericHistogram = React__default.memo(({ data, height, width, condition, onChange, isSmall = false }) => {
    const yMax = height - MARGIN$1.bottom;
    const xMax = width - MARGIN$1.right;
    const yValueMax = useMemo(() => {
        return Math.max(...data.bars.map(({ y }) => y.reduce((sum, y) => sum + y, 0)));
    }, [data]);
    const xRange = useMemo(() => {
        const range = { min: Infinity, max: -Infinity, delta: 0 };
        for (const { x } of data.bars) {
            if (x[0] < range.min) {
                range.min = x[0];
            }
            if (x[1] > range.max) {
                range.max = x[1];
            }
        }
        range.delta = (range.max - range.min) * 0.01 || range.max * 0.01;
        return range;
    }, [data]);
    const yScale = useMemo(() => {
        return scaleLinear({ range: [MARGIN$1.top, yMax], domain: [yValueMax, 0] });
    }, [yMax, yValueMax]);
    const xScale = useMemo(() => {
        return scaleLinear({
            range: [MARGIN$1.left, xMax],
            domain: [xRange.min - xRange.delta, xRange.max + xRange.delta],
        });
    }, [xMax, xRange.delta, xRange.max, xRange.min]);
    return (jsxs("svg", { className: modules_30e9243f.plot, height: height, width: width, xmlns: "http://www.w3.org/2000/svg", children: [yValueMax <= 0 && jsx(EmptyHistogramNotification, { height: height, width: width }), yValueMax > 0 && (jsxs(Group, { children: [jsx(Group, { children: jsx(NumericSlicedBar, { data: data, numericBarGap: isSmall ? 0 : undefined, xScale: xScale, yScale: yScale }) }), jsx(Group, { left: MARGIN$1.left, children: jsx(AxisLeft, { hideZero: true, numTicks: isSmall ? SMALL_NUM_TICKS_Y : NUM_TICKS_Y, scale: yScale }) }), jsx(Group, { top: yMax, children: jsx(AxisBottom, { numTicks: isSmall ? SMALL_NUM_TICKS_X : undefined, scale: xScale, tickFormat: tickFormatter, tickLabelProps: tickLabelProps }) }), jsx(NumericHistogramInput, { condition: condition, dataType: 'numeric', height: height, width: width, xScale: xScale, onChange: onChange })] }))] }));
});

var css$1 = ".DiscreteHistogram_module_bar__aa50775b {\n  cursor: pointer;\n}\n.DiscreteHistogram_module_bar__aa50775b:hover [data-selected=\"false\"] {\n  opacity: 0.7;\n}\n.DiscreteHistogram_module_bar__aa50775b:hover [data-selected=\"true\"] {\n  opacity: 0.9;\n}\n.DiscreteHistogram_module_bar__aa50775b [data-selected=\"false\"] {\n  opacity: 0.5;\n}\n.DiscreteHistogram_module_bar__aa50775b [data-selected=\"true\"] {\n  opacity: 1;\n}\n.DiscreteHistogram_module_bar__aa50775b rect {\n  transition: opacity 0.2s;\n}\n.DiscreteHistogram_module_plot__aa50775b {\n  user-select: none;\n}\n";
var modules_2a9bc0a2 = {"bar":"DiscreteHistogram_module_bar__aa50775b","plot":"DiscreteHistogram_module_plot__aa50775b"};
n(css$1,{});

const DiscreteHistogram = (props) => {
    const { condition, onChange, height, width, data, isSmall = false } = props;
    const yValueMax = useMemo(() => {
        return Math.max(...data.bars.map(({ y }) => y.reduce((sum, y) => sum + y, 0)));
    }, [data]);
    const xValues = useMemo(() => {
        const conditionValues = condition ? condition.values.map((x) => x.value) : [];
        const datasetMissingValues = data.bars
            .filter((bar) => conditionValues.indexOf(bar.x) === -1)
            .map((bar) => bar.x);
        return [...conditionValues, ...datasetMissingValues];
    }, [condition, data.bars]);
    const yScale = useMemo(() => {
        return scaleLinear({
            range: [MARGIN$1.top, height - MARGIN$1.bottom],
            domain: [yValueMax, 0],
        });
    }, [height, yValueMax]);
    const xScale = useMemo(() => {
        return scaleBand({
            range: [MARGIN$1.left, width - MARGIN$1.right],
            domain: xValues,
            padding: isSmall ? 0 : 0.2,
        });
    }, [isSmall, width, xValues]);
    const conditionMap = useMemo(() => {
        return condition
            ? condition.values.reduce((acc, item) => {
                acc[item.value] = item.isSelected;
                return acc;
            }, {})
            : {};
    }, [condition]);
    const onBarClick = useCallback((value) => {
        if (!condition || !onChange)
            return;
        const isSelected = !!conditionMap?.[value];
        const newCondition = {
            ...condition,
            values: condition.values.map((x) => {
                if (x.value === value) {
                    return { ...x, isSelected: !isSelected };
                }
                return x;
            }),
        };
        onChange(newCondition);
    }, [condition, conditionMap, onChange]);
    const yMax = height - MARGIN$1.bottom;
    const selectBarComponent = useMemo(() => ({ barWidth, barX, bar }) => {
        return (jsx("rect", { cursor: "pointer", fill: conditionMap[bar.x] ? 'transparent' : 'rgba(255,255,255,.3)', height: yMax - MARGIN$1.top, width: barWidth, x: barX, y: MARGIN$1.top, onClick: () => onBarClick(bar.x) }));
    }, [conditionMap, onBarClick, yMax]);
    return (jsxs("svg", { className: modules_2a9bc0a2.plot, height: height, width: width, xmlns: "http://www.w3.org/2000/svg", children: [yValueMax <= 0 && jsx(EmptyHistogramNotification, { height: height, width: width }), yValueMax > 0 && (jsxs(Group, { children: [jsx(Group, { children: jsx(DiscreteSlicedBar, { component: condition ? selectBarComponent : undefined, data: data, xScale: xScale, yScale: yScale }) }), jsx(Group, { left: MARGIN$1.left, children: jsx(AxisLeft, { hideZero: true, numTicks: isSmall ? SMALL_NUM_TICKS_Y : NUM_TICKS_Y, scale: yScale }) }), jsx(Group, { top: height - MARGIN$1.bottom, children: jsx(AxisBottom, { numTicks: isSmall ? SMALL_NUM_TICKS_X : NUM_TICKS_X, scale: xScale, tickComponent: ({ formattedValue, ...props }) => (jsxs("text", { ...props, dy: "0.45em", transform: `rotate(${props.angle}, ${props.x}, ${props.y})`, children: [formattedValue && formattedValue?.length > 7 && (jsx("title", { children: formattedValue })), truncate(String(formattedValue), { length: 7 })] })), tickLabelProps: tickLabelProps }) })] }))] }));
};

function isNumericHistogramData(data) {
    return data.type === 'numeric';
}
function isDiscreteHistogramData(data) {
    return data.type === 'discrete';
}
function isDateHistogramData(data) {
    return data.type === 'date';
}

const PADDING = 1;
const SINGLE_BAR_WIDTH = 8;
const HistogramBar = memo((props) => {
    const { xScale, yScale, bar, yMax, ...rest } = props;
    let barX = xScale(bar.x0);
    let barWidth = Math.abs(xScale(bar.x) - barX) - PADDING;
    if (barWidth < 0) {
        barWidth = SINGLE_BAR_WIDTH;
        barX -= SINGLE_BAR_WIDTH / 2;
    }
    const barHeight = Math.abs(yMax - yScale(bar.y));
    const barY = yMax - barHeight;
    return (jsx(Bar, { fill: "rgb(18, 147, 154)", height: barHeight, width: barWidth, x: barX, y: barY, ...rest }));
});

function Linear(context) {
  this._context = context;
}

Linear.prototype = {
  areaStart: function() {
    this._line = 0;
  },
  areaEnd: function() {
    this._line = NaN;
  },
  lineStart: function() {
    this._point = 0;
  },
  lineEnd: function() {
    if (this._line || (this._line !== 0 && this._point === 1)) this._context.closePath();
    this._line = 1 - this._line;
  },
  point: function(x, y) {
    x = +x, y = +y;
    switch (this._point) {
      case 0: this._point = 1; this._line ? this._context.lineTo(x, y) : this._context.moveTo(x, y); break;
      case 1: this._point = 2; // proceed
      default: this._context.lineTo(x, y); break;
    }
  }
};

function curveLinear(context) {
  return new Linear(context);
}

var css = ".HistogramView_module_plot__b426c45a svg text {\n  user-select: none;\n}\n.HistogramView_module_histogramColumn__b426c45a {\n  margin-right: -10px;\n}\n.HistogramView_module_histogramColumn__b426c45a .rv-xy-plot__axis--horizontal .rv-xy-plot__axis__ticks {\n  transform: translate(0, 2px);\n}\n";
var modules_d7601c33 = {"plot":"HistogramView_module_plot__b426c45a","histogramColumn":"HistogramView_module_histogramColumn__b426c45a"};
n(css,{});

const MOVE_THRESHOLD = 5;
const usePoints = (args) => {
    const { yScale, xScale, move, add, isEditable, maxIndex, remove, yMultiplier } = args;
    const [currentPoint, setCurrentPoint] = useState(null);
    const getPosition = useCallback((event) => {
        const { x, y } = localPoint(event) ?? { x: 0, y: 0 };
        const xValue = xScale.invert(x);
        const yValue = yScale.invert(y) / yMultiplier;
        return { x: xValue, y: yValue };
    }, [xScale, yMultiplier, yScale]);
    const onDragMove = useCallback(({ event }) => {
        if (currentPoint !== null && currentPoint.originalPointIndex !== undefined) {
            const { x, y } = getPosition(event);
            move(x, y, currentPoint.originalPointIndex, currentPoint.id);
        }
    }, [currentPoint, getPosition, move]);
    const onDragEnd = useCallback(() => setCurrentPoint(null), []);
    const onDragStart = useCallback(({ x = 0, y = 0 }, item) => {
        if (item.originalPointIndex !== undefined) {
            setCurrentPoint(item);
        }
    }, []);
    const onPointAdd = useCallback((event) => {
        if (isEditable) {
            const { x, y } = getPosition(event);
            add(x, y, maxIndex);
        }
    }, [add, getPosition, isEditable, maxIndex]);
    const onPointDelete = useCallback(({ x = 0, y = 0 }, item) => {
        const isPointMoved = Math.abs(x - Number(currentPoint?.x ?? 0)) >= MOVE_THRESHOLD ||
            Math.abs(y - (currentPoint?.y ?? 0)) >= MOVE_THRESHOLD;
        if (item.originalPointIndex && isEditable && !isPointMoved) {
            remove(item.originalPointIndex);
        }
    }, [currentPoint?.x, currentPoint?.y, isEditable, remove]);
    return { onDragMove, onDragEnd, onDragStart, onPointAdd, onPointDelete };
};

const NUM_TICKS = 5;
const MARGIN = { left: 36, right: 36, top: 10, bottom: 36 };
const DISCRETE_X_PADDING = 0.25;
const desirabilityScaleFormatter = new Intl.NumberFormat('en-En', {
    style: 'decimal',
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
});
const barsRerenderDefault = ({ bars, xScale, yMax, yHistogramScale, }) => {
    return bars.map((bar) => {
        return (jsx(HistogramBar, { bar: bar, xScale: xScale, yMax: yMax, yScale: yHistogramScale }, bar.x));
    });
};
const HistogramView = React__default.memo(({ desirability, metadata, points, height, width, isStatic, onAddFunctionPoint, onUpdateFunctionPoint, onRemoveFunctionPoint, onMoveFinish, barsRerender, }) => {
    const isDiscreteColumn = metadata.isDiscreteColumn;
    const type = desirability.type;
    const params = desirability.functionParams[type];
    const isDiscreteDesirabilityType = type === DesirabilityFunctionType.discrete;
    const discretePoints = useMemo(() => {
        if (isDiscreteColumn && points) {
            return points;
        }
        else if (isDiscreteColumn) {
            return (desirability.functionParams[DesirabilityFunctionType.discrete]?.points ?? []);
        }
        else {
            return [];
        }
    }, [desirability.functionParams, isDiscreteColumn, points]);
    const { discreteTicks, discreteBars, discreteMaxBar } = useMemo(() => {
        return {
            discreteTicks: discretePoints.map((point) => {
                return String(point.x);
            }),
            discreteBars: discretePoints.map((point, index) => ({
                x0: index - DISCRETE_X_PADDING,
                x: index + DISCRETE_X_PADDING,
                y0: 0,
                y: point.count,
            })),
            // assign 1 to the discreteMaxBar in the case, when we replace absented in the dataset column
            // with the another one existed in the dataset column
            // in this case every point has count === 0, which leads to an errors, when create histogram axios
            discreteMaxBar: Math.max(...discretePoints.map((point) => point.count)) || 1,
        };
    }, [discretePoints]);
    const maxHistogram = useMemo(() => {
        const data = isDiscreteDesirabilityType ? discreteMaxBar : metadata.histogramMax;
        return calculateHistogramTicks(data).at(-1) ?? 0;
    }, [discreteMaxBar, isDiscreteDesirabilityType, metadata.histogramMax]);
    const range = useMemo(() => {
        if (isDiscreteDesirabilityType) {
            return {
                min: -0.5,
                max: points ? points.length - 0.5 : params.points.length - 0.5,
            };
        }
        return SCORING_FUNCTIONS_RULES[type].getRange(params, metadata);
    }, [isDiscreteDesirabilityType, type, params, metadata, points]);
    const yMax = height - MARGIN.bottom;
    const xMax = width - MARGIN.right;
    const yHistogramScale = useMemo(() => scaleLinear({ range: [MARGIN.bottom, yMax], domain: [maxHistogram, 0] }), [maxHistogram, yMax]);
    const yDesirabilityScale = useMemo(() => scaleLinear({ range: [MARGIN.bottom, yMax], domain: [maxHistogram, 0] }), [maxHistogram, yMax]);
    const xScaleLinear = useMemo(() => scaleLinear({ range: [MARGIN.left, xMax], domain: [range.min, range.max] }), [xMax, range.min, range.max]);
    const xScaleDiscreteAxis = scaleBand({
        range: [MARGIN.left, xMax],
        domain: discreteTicks,
    });
    const linePoints = useMemo(() => {
        const points = SCORING_FUNCTIONS_RULES[type].toLine(params, range);
        return points.map((point) => ({
            x: xScaleLinear(Number(point.x)),
            y: yDesirabilityScale(point.y * maxHistogram),
            id: point.id,
            opacity: !point.mark ? 0 : 1,
            originalPointIndex: point.originalPointIndex,
        }));
    }, [type, params, range, xScaleLinear, yDesirabilityScale, maxHistogram]);
    const { onDragStart, onPointAdd, onDragMove, onPointDelete, onDragEnd } = usePoints({
        xScale: xScaleLinear,
        yScale: yDesirabilityScale,
        add: onAddFunctionPoint,
        move: onUpdateFunctionPoint,
        remove: onRemoveFunctionPoint,
        maxIndex: linePoints.length,
        isEditable: type === DesirabilityFunctionType.custom,
        yMultiplier: maxHistogram,
    });
    const [state, increment] = useReducer((x) => x + 1, 0); // to rerender a dragged circle with updated state
    const onDragEndOutside = useCallback((event) => {
        event.stopPropagation(); // to prevent point adding after click/drag
        onMoveFinish();
        setTimeout(() => {
            onDragEnd();
            increment();
        }, 0);
        document.removeEventListener('pointerup', onDragEndOutside);
    }, [onDragEnd, onMoveFinish]);
    const xScale = useMemo(() => (isDiscreteDesirabilityType ? xScaleDiscreteAxis : xScaleLinear), [isDiscreteDesirabilityType, xScaleDiscreteAxis, xScaleLinear]);
    const bars = isDiscreteDesirabilityType ? discreteBars : metadata.histogramBars;
    return (jsxs("svg", { className: modules_d7601c33.plot, height: height, viewBox: `0 0 ${width} ${height}`, width: width, xmlns: "http://www.w3.org/2000/svg", children: [jsx(Group, { left: MARGIN.left, children: jsx(AxisLeft, { hideZero: true, numTicks: NUM_TICKS, scale: yHistogramScale }) }), jsx(Group, { left: width - MARGIN.right, children: jsx(AxisRight, { numTicks: NUM_TICKS, scale: yDesirabilityScale, tickFormat: (value) => desirabilityScaleFormatter.format(Number(value) / maxHistogram) }) }), jsx(Group, { top: yMax, children: jsx(AxisBottom, { numTicks: NUM_TICKS, scale: xScaleLinear, tickLabelProps: () => ({
                        angle: -45,
                        fontSize: 10,
                        textAnchor: 'middle',
                    }), ...(isDiscreteDesirabilityType && {
                        scale: xScaleDiscreteAxis,
                        tickSize: 0,
                        numTicks: discreteTicks.length,
                    }), tickComponent: ({ formattedValue, ...props }) => (jsxs("text", { ...props, dy: "0.45em", transform: `rotate(${props.angle}, ${props.x}, ${props.y})`, children: [formattedValue && formattedValue?.length > 7 && (jsx("title", { children: formattedValue })), truncate(String(formattedValue), { length: 7 })] })) }) }), barsRerender
                ? barsRerender(xScale, yHistogramScale)
                : barsRerenderDefault({ bars, xScale: xScaleLinear, yMax, yHistogramScale }), jsx(LinePath, { curve: curveLinear, data: linePoints, stroke: "red", strokeWidth: 3, x: (data) => data.x, y: (data) => data.y }), jsx("rect", { fill: "transparent", height: Math.max(yMax, 0), width: Math.max(xMax, 0), x: MARGIN.left, y: MARGIN.top, onMouseUp: onPointAdd }), !isStatic &&
                linePoints.map((item) => {
                    if (item.originalPointIndex === undefined) {
                        return null;
                    }
                    return (jsx(Drag, { height: height, width: width, x: Number(item.x), y: item.y, onDragEnd: (event) => {
                            document.removeEventListener('pointerup', onDragEndOutside); // drag was handled by drag element, so we don't need to handle it outside SVG
                            event.event.stopPropagation(); // to prevent point adding after click/drag
                            onDragEnd();
                            onMoveFinish();
                            onPointDelete(event, item);
                            increment();
                        }, onDragMove: onDragMove, onDragStart: (event) => {
                            onDragStart(event, item);
                            document.addEventListener('pointerup', onDragEndOutside);
                        }, children: ({ dragStart, dragEnd, dragMove, x = 0, y = 0, dx, dy }) => {
                            const boundedY = Math.max(MARGIN.bottom, y); // sometimes y is less than minimal value
                            const xBoundary = Math.min(xMax, Math.max(xMax, MARGIN.left, x + dx));
                            const yBoundary = Math.min(yMax, Math.max(yMax, MARGIN.bottom, boundedY + dy));
                            const finalDx = xBoundary === x ||
                                item.x === x ||
                                x + dx < xBoundary ||
                                x + dx > xBoundary
                                ? 0
                                : dx;
                            const finalDy = yBoundary === boundedY ||
                                item.y === boundedY ||
                                boundedY + dy < yBoundary ||
                                boundedY + dy > yBoundary
                                ? 0
                                : dy;
                            return (jsx("circle", { cx: x, cy: boundedY, ...(item.opacity === 0 && { style: { pointerEvents: 'none' } }), fill: "transparent", opacity: item.opacity, r: 5, stroke: "blue", strokeWidth: 3, transform: `translate(${finalDx}, ${finalDy})`, onMouseDown: dragStart, onMouseMove: dragMove, onMouseUp: dragEnd, onTouchEnd: dragEnd, onTouchMove: dragMove, onTouchStart: dragStart }, `dot-${item.id}-${type}`));
                        } }, `drag-${type}-${item.id}-${state}-${item.originalPointIndex}`));
                })] }));
});

const margin = { top: 40, right: 5, bottom: 25, left: 2 };
const BoxPlotComponent = ({ label, statistic, width, height, fillColor, strokeColor, globalMin, globalMax, }) => {
    const { firstQuartile, median, thirdQuartile, outliers, min, max, lowerBound, upperBound, } = statistic;
    const xScaleMin = useMemo(() => globalMin ?? min ?? 0, [globalMin, min]);
    const xScaleMax = useMemo(() => globalMax ?? max ?? 0, [globalMax, max]);
    const emptyGraph = min === undefined || max === undefined;
    const xDelta = useMemo(() => {
        return (xScaleMax - xScaleMin) * 0.05 || xScaleMax * 0.05;
    }, [xScaleMax, xScaleMin]);
    const xScale = useMemo(() => scaleLinear({
        range: [margin.left, width - margin.right],
        round: true,
        domain: [xScaleMin - xDelta, xScaleMax + xDelta],
    }), [width, xScaleMin, xDelta, xScaleMax]);
    const gradientId = `bxplot-grad-${label}`;
    const gradient = useMemo(() => {
        if (!Array.isArray(fillColor))
            return [];
        const [min, max] = xScale.domain();
        return fillColor.map((value) => ({
            color: rgbToString(value.color),
            percent: `${Math.floor((100 * (value.value - min)) / (max - min))}%`,
        }));
    }, [fillColor, xScale]);
    return (jsxs("svg", { height: height, width: width, children: [Array.isArray(fillColor) && (jsx("defs", { children: jsx("linearGradient", { gradientUnits: "userSpaceOnUse", id: gradientId, x1: "0", x2: width, y1: "0", y2: "0", children: gradient.map((grad, index) => (jsx("stop", { offset: grad.percent, "stop-color": grad.color }, index))) }) })), !emptyGraph && (jsx(BoxPlot, { boxWidth: 20, fill: Array.isArray(fillColor) ? `url(#${gradientId})` : fillColor, firstQuartile: firstQuartile, horizontal: true, max: upperBound, median: median, min: lowerBound, outliers: outliers, stroke: strokeColor, thirdQuartile: thirdQuartile, valueScale: xScale }, label)), jsx(Group, { left: margin.left, children: jsx(AxisBottom, { numTicks: 4, scale: xScale, top: height - margin.bottom }) })] }));
};

export { BoxPlotComponent, DateHistogram, DiscreteHistogram, EmptyHistogramNotification, HistogramBar, HistogramView, MARGIN$1 as MARGIN, NUM_TICKS_X, NUM_TICKS_Y, NumericHistogram, SMALL_NUM_TICKS_X, SMALL_NUM_TICKS_Y, TOOLTIP, dateTickFormatter, formatDateForHistogram, getBarsRerenderForDesirabilityHistogram, isDateHistogramData, isDiscreteHistogramData, isNumericHistogramData, tickFormatter, tickLabelProps, toPrecisionString };
//# sourceMappingURL=index.js.map
